import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import saveAs from "file-saver";


const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export enum ServiceProviderInvoiceStatus {
  PENDING = "PENDING",
  GENERATED = "GENERATED",
  CONFIRMED = "CONFIRMED",
  ERROR = "ERROR",
}

export interface ServiceProviderInvoiceCost {
  id: number;
  mnpCount: number;
  hlrCount: number;
  mnpTestingCount: number;
  hlrTestingCount: number;
  isDeleted: boolean;
  mnpTotalCost: number;
  hlrTotalCost: number;
  mnpTestingCost: number;
  hlrTestingCost: number;
  totalCount: number;
  totalCost: number;
  serviceProviderId: number;
  serviceProviderInvoiceId: number;
}

export interface ServiceProviderBillType {
  id: number;
  type: string;
  dueDate: Date;
  startDate: Date;
  endDate: Date;
  profileId: string | string[];
  status: ServiceProviderInvoiceStatus;
  confirmedAmount: number;
  amount: number;
  serviceProviderInvoiceCost: ServiceProviderInvoiceCost[];
  createdDate: Date;
  invoiceDate: Date;  
  excludeZero: boolean;
  ref: string;
  updatedBy?: string;
  historyTimestamp: Date;
  createdBy: string;
  confirmedBy: string;
  isDeleted: boolean;
  version: number;
  timestamp: Date;
  sentBy?: string;
}

export interface ServiceProviderBillHistory {
  id: string;
  snapshot: ServiceProviderBillType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  confirmedBy: string;
}


export async function fetchServiceProviderBill(
  params: Params<ServiceProviderBillType>
): Promise<IPageable<ServiceProviderBillType>> {
  try {
    const response = await axiosInstance.get("/invoices/service-providers", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the service provider invoices."
    );
  }
}

export async function fetchServiceProviderBillById(
  id: ServiceProviderBillType["id"]
): Promise<ServiceProviderBillType> {
  try {
    const response = await axiosInstance.get(`/invoices/service-providers/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting service provider invoice ${id}.`
    );
  }
}

export async function fetchConfirmedServiceProviderBills(params: {
  page: number;
  size: number;
}): Promise<{ content: { id: string; customerName: string }[] }> {
  try {
    const response = await axiosInstance.get("/invoices/service-providers", {
      params: { ...params, status: "CONFIRMED" },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching service provider bills:", error);
    throw error;
  }
}

export interface BulkGenerateRequest {
  connectionProfileIds: number[];
  periods: { month: number; year: number }[];
}

export interface BulkGenerateResult {
  month: number;
  year: number;
  invoiceId: number;
  invoiceRef: string;
  success: boolean;
  errorMessage?: string;
}

export interface BulkGenerateResponse {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  results: BulkGenerateResult[];
}

export const createServiceProviderBill = async (
  params: BulkGenerateRequest
): Promise<BulkGenerateResponse> => {
  try {
    const response = await axiosInstance.post(`/invoices/service-providers/bulk-generate`, params);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.errorMessage ||
        "An error occurred while generating service provider bills."
    );
  }
};

export const confirmServiceProviderApi = async (
  id: string | number,
  amount: number,
  ref: string
): Promise<ServiceProviderBillType> => {
  try {
    const response = await axiosInstance.post(
      `/invoices/service-providers/${id}/confirm`,
      null,
      {
        params: { amount, ref },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while confirming the service provider bill."
    );
  }
};

export const fetchServiceProvidersByProfile = async (profileId: string) => {
  try {
    const response = await axiosInstance.get(
      `/service-providers`, 
      { params: { profileId, isDeleted: false, enabled: true } }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching service providers for profile:", error);
    throw error;
  }
};

export const useServiceProviderBillQuery = (
  params: Params<ServiceProviderBillType>
): UseQueryResult<IPageable<ServiceProviderBillType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["serviceProviderBill", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchServiceProviderBill(effectiveParams),
    // Poll every 2 seconds while any invoice has PENDING status
    refetchInterval: (query) => {
      const data = (query as any)?.state?.data as IPageable<ServiceProviderBillType> | undefined;
      const hasPending = data?.content?.some(
        (item) => item.status === ServiceProviderInvoiceStatus.PENDING || item.status === ("PENDING" as any)
      );
      return hasPending ? 2000 : false;
    },
  });
};

export const useServiceProviderBillById = (
  id: ServiceProviderBillType["id"]
): UseQueryResult<ServiceProviderBillType, Error> => {
  return useQuery<ServiceProviderBillType, Error>({
    queryKey: ["serviceProviderBillById", id],
    queryFn: () => fetchServiceProviderBillById(id),
  });
};

export const exportServiceProviderBill = async (
  params: Params<ServiceProviderBillType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/invoices/service-providers/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "Service Provider Bill.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error exporting service provider bills:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchServiceProviderBillHistory(
  id: ServiceProviderBillType["id"],
  params: Params<ServiceProviderBillType> = {}
): Promise<IPageable<ServiceProviderBillType>> {
  try {
    const response = await axiosInstance.get(`/billings/history`, { params: { mainId: id , sort: "historyTimestamp,desc" , ...params } });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Service Provider Bill history for ID ${id}.`
    );
  }
}

export const pdfServiceProviderApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/invoices/service-providers/${id}/pdf`, {
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = `service-provider-invoice-${id}.pdf`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });

    saveAs(blob, filename);

    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching the PDF:",
      error.response?.status,
      error.response?.data
    );
  }
};

export const pdfServiceProviderDetailsApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(
      `/invoices/service-providers/${id}/pdf/details`,
      {
        responseType: "blob",
      }
    );

    const disposition = response.headers["content-disposition"];
    let filename = `service-provider-invoice-details-${id}.pdf`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });

    saveAs(blob, filename);

    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching the PDF:",
      error.response?.status,
      error.response?.data
    );
  }
};

export const deleteServiceProviderBills = async (
  ids: string[]
): Promise<void> => {
  try {
    const response = await axiosInstance.delete(`/invoices/service-providers`, { data: ids });
    // Check if the response contains an error message (some APIs return 200 with error in body)
    if (response.data?.error || response.data?.message) {
      throw new Error(response.data.message || response.data.error);
    }
    console.log(`Service Provider Bills with IDs ${ids} deleted successfully.`);
  } catch (error: any) {
    // Extract error message from axios error response or use the error message directly
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An error occurred while deleting the service provider bills.";
    throw new Error(errorMessage);
  }
};
